import React, { createContext, useState, useContext, useEffect } from 'react';
import api from "../services/api";
import {getSystemID} from "./authProvider";

const AppStateContext = createContext();

// Nombre de notifications récupérées pour le tableau de bord (qui n'en affiche que 5,
// mais la liste sert aussi au compteur de la barre de navigation).
const NOTIFICATIONS_HOME_LIMIT = 20;

// Fréquence de rafraîchissement des raccourcis liés à un expose Zigbee (ms).
const SHORTCUTS_REFRESH_INTERVAL = 3000;

const EMPTY_COUNTS ={ total: 0, byCategory: {}, totalByCategory: {} };

export const useAppContext = () => useContext(AppStateContext);

export const AppProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [notificationCounts, setNotificationCounts] = useState(EMPTY_COUNTS);
  const [systemsAvailable, setAvailableSystems] = useState([]);
  const [system, setSystem] = useState(null);
  const [user, setUser] = useState(null);
  const [userLoaded, setUserLoaded] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [shortcuts, setShortcuts] = useState([]);
  const [timelapses, setTimelapses] = useState([]);
  const [records, setRecords] = useState([]);


  const countNotification = ()  => {
    return notifications.filter(notification => notification.seen_at === null).length
  }

  // Liste non filtrée, utilisée par le tableau de bord et le compteur de la barre de
  // navigation. La page Notifications gère sa propre liste filtrée et paginée.
  const fetchNotifications = async () => {
    try {
      const response = await api.get(`/notifications?limit=${NOTIFICATIONS_HOME_LIMIT}`);

      // L'ordre vient du serveur (alertes non lues en tête puis date décroissante) :
      // ne pas retrier ici, cela écraserait la priorité des alertes.
      setNotifications(response.data.notifications);
    } catch (error) {
      console.error("Failed to fetch notifications", error);
      setNotifications([]);
    }
  };

  const fetchNotificationCounts = async () => {
    try {
      const response = await api.get('/notifications/count');
      setNotificationCounts(response.data);
    } catch (error) {
      console.error("Failed to fetch notification counts", error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchNotificationCounts();
  }, []);

  const fetchShortcuts = async () => {
    try {
      const response = await api.get(`/shortcuts`);
      setShortcuts(response.data);
    } catch (error) {
      console.error(error);
      setShortcuts([]);
    }
  };

  useEffect(() => {
    fetchShortcuts();
  }, []);

  // L'état des exposes Zigbee arrive avec /shortcuts : un seul rafraîchissement
  // périodique pour toutes les tuiles, uniquement s'il existe un raccourci Zigbee.
  const hasZigbeeShortcut = shortcuts.some(
    (shortcut) => shortcut.zigbeeExpose || shortcut.zigbeeDisplayExpose
  );

  useEffect(() => {
    if (!hasZigbeeShortcut) {
      return;
    }

    const intervalId = setInterval(fetchShortcuts, SHORTCUTS_REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, [hasZigbeeShortcut]);

  useEffect(() => {
    console.log('getUser');
    const getUser = async () => {
      console.log('getUser');
      try {
        const response = await api.get(`/users/me`);
        setUser(response.data);
      } catch (error) {
        console.log('ERROR GET USER');
        console.error(error);
        setUser(null);
      } finally {
        setUserLoaded(true);
      }
    };

    getUser();
  }, []);

  useEffect(() => {
    const getTimelapses = async () => {
      try {
        const response = await api.get(`/timelapses`);
        console.log('Timelapses', response.data)
        setTimelapses(response.data);
      } catch (error) {
        console.error(error);
        setTimelapses([]);
      }
    };

    getTimelapses();
  }, []);

  useEffect(() => {
    const getRecords = async () => {
      try {
        const response = await api.get(`/records?origin=manual`);
        setRecords(response.data);
      } catch (error) {
        console.error(error);
        setRecords([]);
      }
    };

    getRecords();
  }, []);

  useEffect(() => {
    const getCurrentSystem = async () => {
      try {
        const systemID = getSystemID();
        const response = await api.get(`/systems/${systemID}`);
        console.log('Système', response.data)
        setSystem(response.data);
      } catch (error) {
        console.error(error);
        setSystem(null);
      }
    };

    getCurrentSystem();
  }, []);

  useEffect(() => {
    fetchCameras();
  }, []);

  const fetchCameras = async () => {
    try {
      const response = await api.get('/cameras');
      setCameras(response.data);
    } catch (error) {
      console.error("Failed to fetch cameras", error);
      setCameras([]);
    }
  };

  useEffect(() => {
    const fetchSystems = async () => {
      try {
        const response = await api.get('/systems');
        const result = [];

        for(const theSystem of response.data) {
          result.push({
            image: 'https://cdn.codnum.fr/watch4safe/assets/images/favicon.svg',
            icon: false,
            title: theSystem.name,
            link: ``,
            id : theSystem.id,
          });
        }
        setAvailableSystems(result);
      } catch (error) {
        setAvailableSystems([]);
      }
    };

    fetchSystems();
  }, []);

  return (
    <AppStateContext.Provider value={{countNotification, systemsAvailable, cameras, shortcuts, fetchShortcuts, notifications, setNotifications, notificationCounts, fetchNotifications, fetchNotificationCounts, system, setSystem, timelapses, setTimelapses, records, setRecords, user, setUser, userLoaded, fetchCameras}}>
      {children}
    </AppStateContext.Provider>
  );
};
